<template>
    <section class="member-requests">
        <div class="member-requests__container container-fluid">
            <header class="member-requests__header">
                <h2 class="member-requests__title">
                    <button
                        type="button"
                        class="btn back_btn"
                        aria-label="Go back"
                        @click="goBack"
                    >
                        <i
                            class="fa fa-chevron-left"
                            aria-hidden="true"
                        ></i>
                    </button>

                    <span>Member Requests</span>
                </h2>
            </header>

            <div
                class="member-requests__table-wrapper table-responsive"
            >
                <div
                    v-if="isInitialLoading"
                    class="member-requests__loading"
                    role="status"
                >
                    <div class="spinner spinner-1"></div>
                    <span class="sr-only">
                        Loading member requests
                    </span>
                </div>

                <div
                    v-else-if="loadError"
                    class="member-requests__error"
                    role="alert"
                >
                    <p>{{ loadError }}</p>

                    <button
                        type="button"
                        class="btn btn-info"
                        @click="loadRequests"
                    >
                        Retry
                    </button>
                </div>

                <table
                    v-else
                    class="
                        member-requests__table
                        table
                        meeting_main_table
                    "
                >
                    <thead>
                        <tr>
                            <th scope="col">Name</th>
                            <th scope="col">Country</th>
                            <th scope="col">City</th>
                            <th scope="col">
                                Hospital/Clinic/Lab
                            </th>
                            <th scope="col">Department</th>
                            <th scope="col">Message</th>
                            <th scope="col" class="text-right">Action</th>
                        </tr>
                    </thead>

                    <tbody v-if="hasRequests">
                        <tr
                            v-for="request in requests"
                            :key="getRequestKey(request)"
                            class="member-requests__row"
                        >
                            <td data-label="Name">
                                <div class="member-profile">
                                    <img
                                        :src="
                                            getUserThumbnail(
                                                request.from_user
                                            )
                                        "
                                        :alt="
                                            getUserName(
                                                request.from_user
                                            )
                                        "
                                        class="member-profile__avatar"
                                        width="50"
                                        height="50"
                                        @error="handleAvatarError"
                                    >

                                    <span class="member-profile__name">
                                        {{
                                            getUserName(
                                                request.from_user
                                            )
                                        }}
                                    </span>
                                </div>
                            </td>

                            <td data-label="Country">
                                <div class="member-country">
                                    <img
                                        :src="
                                            getCountryFlag(
                                                getProfileValue(
                                                    request.from_user,
                                                    'country'
                                                )
                                            )
                                        "
                                        :alt="
                                            getCountryName(
                                                getProfileValue(
                                                    request.from_user,
                                                    'country'
                                                )
                                            )
                                        "
                                        class="member-country__flag"
                                        width="32"
                                    >

                                    <span class="sr-only">
                                        {{
                                            getCountryName(
                                                getProfileValue(
                                                    request.from_user,
                                                    'country'
                                                )
                                            )
                                        }}
                                    </span>
                                </div>
                            </td>

                            <td data-label="City">
                                {{
                                    getProfileValue(
                                        request.from_user,
                                        'city'
                                    ) || 'N/A'
                                }}
                            </td>

                            <td data-label="Hospital/Clinic/Lab">
                                {{
                                    getProfileValue(
                                        request.from_user,
                                        'area_of_work'
                                    ) || 'N/A'
                                }}
                            </td>

                            <td data-label="Department">
                                {{
                                    getProfileValue(
                                        request.from_user,
                                        'department'
                                    ) || 'N/A'
                                }}
                            </td>

                            <td
                                data-label="Message"
                                class="member-requests__message"
                            >
                                {{ request.message || 'N/A' }}
                            </td>

                            <td data-label="Action">
                                <div class="request-actions">
                                    <button
                                        type="button"
                                        class="
                                            request-actions__button
                                            request-actions__button--approve
                                        "
                                        :disabled="
                                            isRequestProcessing(
                                                request.id
                                            )
                                        "
                                        @click="
                                            openApprovalModal(request)
                                        "
                                    >
                                        <i
                                            v-if="
                                                isRequestProcessing(
                                                    request.id
                                                )
                                            "
                                            class="
                                                fa
                                                fa-spinner
                                                fa-spin
                                            "
                                            aria-hidden="true"
                                        ></i>

                                        <span>Approve</span>
                                    </button>

                                    <button
                                        type="button"
                                        class="
                                            request-actions__button
                                            request-actions__button--reject
                                        "
                                        :disabled="
                                            isRequestProcessing(
                                                request.id
                                            )
                                        "
                                        @click="rejectRequest(request)"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>

                    <tbody v-else>
                        <tr>
                            <td
                                colspan="7"
                                class="member-requests__empty"
                            >
                                You don't have any requests.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div
            v-if="isActionModalOpen"
            class="request-confirmation"
            role="dialog"
            aria-modal="true"
            aria-labelledby="request-confirmation-title"
            @click.self="closeActionModal"
        >
            <div class="request-confirmation__dialog">
                <header class="request-confirmation__header">
                    <h2
                        id="request-confirmation-title"
                        class="request-confirmation__title"
                    >
                        The sender is not in your contact list.
                    </h2>
                </header>

                <p class="request-confirmation__description">
                    Add the sender to your contacts or block them.
                </p>

                <div class="request-confirmation__actions">
                    <button
                        type="button"
                        class="
                            request-confirmation__button
                            request-confirmation__button--block
                        "
                        :disabled="isModalSubmitting"
                        @click="blockSelectedUser"
                    >
                        <i
                            v-if="modalAction === 'block'"
                            class="fa fa-spinner fa-spin"
                            aria-hidden="true"
                        ></i>

                        <span>Block</span>
                    </button>

                    <button
                        type="button"
                        class="
                            request-confirmation__button
                            request-confirmation__button--add
                        "
                        :disabled="isModalSubmitting"
                        @click="approveSelectedRequest"
                    >
                        <i
                            v-if="modalAction === 'approve'"
                            class="fa fa-spinner fa-spin"
                            aria-hidden="true"
                        ></i>

                        <span>Add</span>
                    </button>

                    <button
                        type="button"
                        class="
                            request-confirmation__button
                            request-confirmation__button--close
                        "
                        :disabled="isModalSubmitting"
                        @click="closeActionModal"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { mapGetters } from 'vuex';
import countries from '@/services/country.json';
import toast from '@/services/toast';

const DEFAULT_AVATAR = require('@/assets/images/user2.png');

const COUNTRY_FLAG_BASE_URL =
    'https://567c9b4f06e54c9c9229fe11191a6432.s3.ca-central-1.amazonaws.com/media/shiny';

export default {
    name: 'MemberRequestList',

    data() {
        return {
            isInitialLoading: false,
            loadError: '',
            processingRequestId: null,

            isActionModalOpen: false,
            selectedRequest: null,
            modalAction: ''
        };
    },

    computed: {
        ...mapGetters({
            connectionRequestState:
                'chatConnectionRequestListGetters'
        }),

        requests() {
            const state = this.connectionRequestState;

            if (
                !state ||
                !state.data ||
                !Array.isArray(state.data.data)
            ) {
                return [];
            }

            return state.data.data;
        },

        hasRequests() {
            return this.requests.length > 0;
        },

        isModalSubmitting() {
            return Boolean(this.modalAction);
        }
    },

    mounted() {
        document.addEventListener(
            'keydown',
            this.handleKeydown
        );

        if (!this.connectionRequestState) {
            this.loadRequests();
        }
    },

    beforeDestroy() {
        document.removeEventListener(
            'keydown',
            this.handleKeydown
        );

        document.body.classList.remove(
            'body_right_scroll_remove'
        );
    },

    methods: {
        goBack() {
            this.$router.go(-1);
        },

        async loadRequests() {
            this.isInitialLoading = true;
            this.loadError = '';

            try {
                await this.$store.dispatch(
                    'chatConnectionRequestListFun'
                );
            } catch (error) {
                this.loadError = this.getErrorMessage(
                    error,
                    'Unable to load member requests.'
                );
            } finally {
                this.isInitialLoading = false;
            }
        },

        async refreshRequestData() {
            const results = await Promise.allSettled([
                this.$store.dispatch(
                    'chatConnectionRequestListFun'
                ),
                this.$store.dispatch(
                    'fetchPinnedChatRooms'
                ),
                this.$store.dispatch(
                    'fetchNormalChatRooms'
                )
            ]);

            const requestRefreshResult = results[0];

            if (
                requestRefreshResult.status === 'rejected'
            ) {
                throw requestRefreshResult.reason;
            }
        },

        openApprovalModal(request) {
            if (
                !request ||
                this.processingRequestId !== null
            ) {
                return;
            }

            this.selectedRequest = request;
            this.isActionModalOpen = true;

            document.body.classList.add(
                'body_right_scroll_remove'
            );
        },

        closeActionModal() {
            if (this.isModalSubmitting) {
                return;
            }

            this.isActionModalOpen = false;
            this.selectedRequest = null;
            this.modalAction = '';

            document.body.classList.remove(
                'body_right_scroll_remove'
            );
        },

        async approveSelectedRequest() {
            if (
                !this.selectedRequest ||
                this.isModalSubmitting
            ) {
                return;
            }

            this.modalAction = 'approve';

            try {
                const response =
                    await this.updateConnectionRequest(
                        this.selectedRequest.id,
                        true
                    );

                toast.success(
                    this.getSuccessMessage(
                        response,
                        'Member added successfully.'
                    )
                );

                await this.refreshRequestData();

                this.modalAction = '';
                this.closeActionModal();
            } catch (error) {
                toast.error(
                    this.getErrorMessage(
                        error,
                        'Unable to approve the request.'
                    )
                );

                this.modalAction = '';
            }
        },

        async rejectRequest(request) {
            if (
                !request ||
                this.processingRequestId !== null
            ) {
                return;
            }

            this.processingRequestId = request.id;

            try {
                const response =
                    await this.updateConnectionRequest(
                        request.id,
                        false
                    );

                toast.success(
                    this.getSuccessMessage(
                        response,
                        'Request cancelled successfully.'
                    )
                );

                await this.refreshRequestData();
            } catch (error) {
                toast.error(
                    this.getErrorMessage(
                        error,
                        'Unable to cancel the request.'
                    )
                );
            } finally {
                this.processingRequestId = null;
            }
        },

        async blockSelectedUser() {
            if (
                !this.selectedRequest ||
                !this.selectedRequest.from_user ||
                this.isModalSubmitting
            ) {
                return;
            }

            this.modalAction = 'block';

            try {
                const response =
                    await this.$store.dispatch(
                        'createBlockUserFun',
                        {
                            blocked_user:
                                this.selectedRequest
                                    .from_user.id
                        }
                    );

                toast.success(
                    this.getSuccessMessage(
                        response,
                        'User blocked successfully.'
                    )
                );

                await this.refreshRequestData();

                this.modalAction = '';
                this.closeActionModal();
            } catch (error) {
                toast.error(
                    this.getErrorMessage(
                        error,
                        'Unable to block the user.'
                    )
                );

                this.modalAction = '';
            }
        },

        updateConnectionRequest(requestId, isApproved) {
            return this.$store.dispatch(
                'apporvedConnectionRequestFun',
                {
                    data: {
                        id: requestId,
                        data: {
                            is_approved: isApproved
                        }
                    }
                }
            );
        },

        isRequestProcessing(requestId) {
            if (
                this.processingRequestId === null ||
                requestId === null ||
                requestId === undefined
            ) {
                return false;
            }

            return (
                String(this.processingRequestId) ===
                String(requestId)
            );
        },

        getRequestKey(request) {
            if (!request) {
                return 'unknown-request';
            }

            return (
                request.id ||
                [
                    request.from_user &&
                        request.from_user.id,
                    request.to_user &&
                        request.to_user.id
                ].join('-')
            );
        },

        getUserName(user) {
            if (!user) {
                return 'Unknown user';
            }

            const name = [
                user.salutation || '',
                user.first_name || '',
                user.last_name || ''
            ]
                .join(' ')
                .replace(/\s+/g, ' ')
                .trim();

            return name || user.email || 'Unknown user';
        },

        getUserThumbnail(user) {
            return (
                user &&
                user.profile &&
                user.profile.thumbnail
            ) || DEFAULT_AVATAR;
        },

        getProfileValue(user, property) {
            if (
                !user ||
                !user.profile ||
                !property
            ) {
                return '';
            }

            return user.profile[property] || '';
        },

        handleAvatarError(event) {
            if (!event || !event.target) {
                return;
            }

            event.target.onerror = null;
            event.target.src = DEFAULT_AVATAR;
        },

        normalizeCountry(country) {
            if (!country) {
                return 'in';
            }

            const normalizedCountry =
                String(country).trim().toLowerCase();

            if (countries[normalizedCountry]) {
                return normalizedCountry;
            }

            const countryCode = Object.keys(countries).find(
                function (code) {
                    return (
                        String(countries[code])
                            .trim()
                            .toLowerCase() ===
                        normalizedCountry
                    );
                }
            );

            return countryCode
                ? countryCode.toLowerCase()
                : 'in';
        },

        getCountryFlag(country) {
            const countryCode =
                this.normalizeCountry(country);

            return (
                COUNTRY_FLAG_BASE_URL +
                '/' +
                countryCode +
                '.png'
            );
        },

        getCountryName(country) {
            const countryCode =
                this.normalizeCountry(country);

            const countryName =
                countries[countryCode];

            if (!countryName) {
                return 'India';
            }

            return this.capitalizeWords(countryName);
        },

        capitalizeWords(value) {
            return String(value)
                .toLowerCase()
                .replace(
                    /\b\w/g,
                    function (character) {
                        return character.toUpperCase();
                    }
                );
        },

        getSuccessMessage(response, fallbackMessage) {
            if (response && response.data) {
                return (
                    response.data.msg ||
                    response.data.message ||
                    fallbackMessage
                );
            }

            return fallbackMessage;
        },

        getErrorMessage(error, fallbackMessage) {
            const responseData =
                error &&
                error.response &&
                error.response.data
                    ? error.response.data
                    : error && error.data
                        ? error.data
                        : null;

            if (responseData) {
                if (
                    responseData.data &&
                    Array.isArray(
                        responseData.data.non_field_errors
                    )
                ) {
                    return (
                        responseData.data
                            .non_field_errors[0] ||
                        fallbackMessage
                    );
                }

                return (
                    responseData.msg ||
                    responseData.message ||
                    fallbackMessage
                );
            }

            if (error && error.message) {
                return error.message;
            }

            return fallbackMessage;
        },

        handleKeydown(event) {
            if (
                event.key === 'Escape' &&
                this.isActionModalOpen
            ) {
                this.closeActionModal();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
$primary-text: #ffffff;
$secondary-text: #a9a9a9;
$accent-color: #87cefa;
$highlight-color: #ffd500;
$danger-color: #dc3545;
$success-color: #21a838;
$panel-background: rgba(228, 233, 237, 0.1);
$border-color: rgba(255, 255, 255, 0.12);
$overlay-background: rgba(0, 0, 0, 0.92);

.member-requests {
    color: $primary-text;
    font-family: 'MyriadProRegular', 'Lato', sans-serif;
    background: rgba(0, 0, 0, .6);
    border-radius: 3px;
    padding: 15px;
    min-height: calc(100dvh - 80px);
    position: relative;
    width: 100%;

    &__header {
        display: flex;
        align-items: center;
    }

    &__title {
        display: flex;
        align-items: center;
        margin: 0;
        color: $primary-text;
        font-size: 24px;
        font-weight: 700;
        text-transform: uppercase;
    }

    &__table-wrapper {
        position: relative;
        min-height: 180px;
        margin-top: 16px;
        padding-top: 4px;
    }

    &__loading,
    &__error {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        min-height: 180px;
        text-align: center;
    }

    &__error {
        color: #ff6b6b;

        p {
            margin-bottom: 15px;
        }
    }

    &__table {
        width: 100%;
        margin-bottom: 0;
        border-spacing: 0 3px;
        border-collapse: separate;
        color: $primary-text;
        letter-spacing: 0.5px;

        th {
            padding: 10px 8px;
            border: 0;
            color: $accent-color;
            background-color: $panel-background;
            font-size: 18px;
            font-weight: 600;
            text-transform: uppercase;
            word-spacing: 3px;
        }

        td {
            padding: 10px 8px;
            border: 0;
            color: $primary-text;
            background-color: $panel-background;
            font-size: 18px;
            vertical-align: middle;
        }
    }

    &__message {
        max-width: 250px;
        word-break: break-word;
        white-space: normal;
    }

    &__empty {
        padding: 30px 15px !important;
        font-size: 20px !important;
        text-align: center;
    }
}

.member-profile {
    display: flex;
    align-items: center;
    min-width: 180px;

    &__avatar {
        flex: 0 0 50px;
        width: 50px;
        height: 50px;
        margin-right: 10px;
        border-radius: 50%;
        object-fit: cover;
        background-color: rgba(255, 255, 255, 0.08);
    }

    &__name {
        line-height: 1.3;
        word-break: break-word;
    }
}

.member-country {
    display: flex;
    align-items: center;

    &__flag {
        display: block;
        width: 32px;
        max-height: 24px;
        object-fit: contain;
    }
}

.request-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;

    &__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 72px;
        padding: 5px 10px;
        border: 0;
        border-radius: 3px;
        color: #ffffff;
        font-family: inherit;
        font-size: 16px;
        cursor: pointer;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        .fa {
            margin-right: 5px;
        }

        &:focus {
            outline: 2px solid $highlight-color;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }

        &--approve {
            background-color: #17a2b8;

            &:hover:not(:disabled){
                color: #22292f;
                background-color: $accent-color;
            }
        }

        &--reject {
            background-color: $danger-color;

            &:hover:not(:disabled){
                background-color: #f05b69;
            }
        }
    }
}

.request-confirmation {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
    overflow-y: auto;
    background-color: $overlay-background;

    &__dialog {
        width: 100%;
        max-width: 520px;
        margin: auto;
        padding: 25px;
        border-radius: 5px;
        color: $primary-text;
        background-color: #000000;
        box-shadow: 0 0 13px rgba(255, 213, 0, 0.6);
    }

    &__title {
        margin: 0;
        color: $primary-text;
        font-size: 20px;
        line-height: 1.4;
        text-align: center;
    }

    &__description {
        margin: 12px 0 25px;
        color: $secondary-text;
        font-size: 15px;
        text-align: center;
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
    }

    &__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 100px;
        min-height: 40px;
        padding: 8px 12px;
        border: 0;
        border-radius: 3px;
        color: #ffffff;
        font-family: inherit;
        font-size: 16px;
        cursor: pointer;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        .fa {
            margin-right: 6px;
        }

        &:focus {
            outline: 2px solid $highlight-color;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }

        &--block {
            background-color: rgba(255, 18, 18, 0.65);
        }

        &--add {
            background-color: rgba(33, 228, 0, 0.55);
        }

        &--close {
            background-color: rgba(255, 255, 255, 0.15);
        }

        &:hover:not(:disabled),
        &:focus:not(:disabled) {
            color: #22292f;
            background-color: $highlight-color;
        }
    }
}

@media only screen and (max-width: 767px) {
    .member-requests {
        &__title {
            font-size: 20px;
        }

        &__table {
            display: block;

            thead {
                position: absolute;
                width: 1px;
                height: 1px;
                overflow: hidden;
                clip: rect(0, 0, 0, 0);
            }

            tbody,
            tr,
            td {
                display: block;
                width: 100%;
            }

            tr {
                margin-bottom: 10px;
                overflow: hidden;
                border: 1px solid rgba(
                    228,
                    233,
                    237,
                    0.22
                );
                border-radius: 5px;
            }

            td {
                position: relative;
                min-height: 40px;
                padding: 8px 8px 8px 44% !important;
                border-bottom: 1px solid rgba(
                    228,
                    233,
                    237,
                    0.08
                );
                font-size: 14px;

                &::before {
                    position: absolute;
                    top: 8px;
                    left: 8px;
                    width: 40%;
                    padding-right: 8px;
                    color: $accent-color;
                    content: attr(data-label);
                    font-size: 14px;
                    font-weight: 600;
                    text-align: left;
                    white-space: normal;
                }

                &:last-child {
                    border-bottom: 0;
                }

                &[colspan] {
                    padding-left: 8px !important;

                    &::before {
                        display: none;
                    }
                }
            }
        }

        &__message {
            max-width: none;
        }
    }

    .member-profile {
        min-width: 0;

        &__avatar {
            width: 42px;
            height: 42px;
            flex-basis: 42px;
        }
    }

    .request-actions {
        justify-content: flex-start;
        flex-wrap: wrap;
    }

    .request-confirmation {
        &__dialog {
            padding: 20px 15px;
        }

        &__actions {
            flex-direction: column;
        }

        &__button {
            width: 100%;
        }
    }
}
</style>